import type { ScanReadoutData } from "../../types/game";

interface Props {
  targetId: string;
  scanData?: ScanReadoutData;
}

const SCHEMATIC_ICONS: Record<string, string> = {
  ship: "◊",      // diamond
  planet: "○",    // circle
  anomaly: "✴",   // eight-pointed star
  station: "⬣",   // hexagon
};

function classify(efficiency: number): "ok" | "warn" | "crit" {
  if (efficiency >= 70) return "ok";
  if (efficiency >= 35) return "warn";
  return "crit";
}

function ArcSilhouette({ arcs }: { arcs: NonNullable<ScanReadoutData["shieldArcs"]> }) {
  // Four-arc compass: forward (top), starboard (right), aft (bottom), port (left)
  const slots: { key: keyof typeof arcs; pos: string; label: string }[] = [
    { key: "forward",   pos: "scan-arc-forward",   label: "FWD" },
    { key: "starboard", pos: "scan-arc-starboard", label: "STB" },
    { key: "aft",       pos: "scan-arc-aft",       label: "AFT" },
    { key: "port",      pos: "scan-arc-port",      label: "PRT" },
  ];

  return (
    <div className="scan-silhouette">
      <div className="scan-silhouette-body" aria-hidden>{SCHEMATIC_ICONS.ship}</div>
      {slots.map(s => {
        const v = arcs[s.key];
        if (typeof v !== "number") return <div key={s.key} className={`scan-arc ${s.pos} scan-arc-unknown`} title={`${s.label}: unknown`}>{s.label}</div>;
        const cls = classify(v);
        return (
          <div key={s.key} className={`scan-arc ${s.pos} scan-arc-${cls}`} title={`${s.label} shields: ${v}%`}>
            <span className="scan-arc-label">{s.label}</span>
            <span className="scan-arc-value">{v}%</span>
          </div>
        );
      })}
    </div>
  );
}

export function ScanReadout({ targetId, scanData }: Props) {
  if (!scanData) {
    return (
      <div className="scan-readout">
        <div className="scan-readout-header">
          <span className="scan-readout-title">SENSOR ANALYSIS</span>
          <span className="scan-readout-target">{formatId(targetId)}</span>
        </div>
        <div className="scan-readout-body">
          <div className="scan-readout-scanning">
            <div className="scan-readout-sweep" />
            <span>Processing sensor data...</span>
          </div>
        </div>
      </div>
    );
  }

  const icon = SCHEMATIC_ICONS[scanData.schematicType ?? "ship"] ?? SCHEMATIC_ICONS.ship;
  const hasArcs = !!scanData.shieldArcs && Object.values(scanData.shieldArcs).some(v => typeof v === "number");
  const subsystemRows = scanData.subsystems
    ? Object.entries(scanData.subsystems).map(([name, val]) => ({ name, ...val }))
    : [];

  return (
    <div className="scan-readout">
      <div className="scan-readout-header">
        <span className="scan-readout-title">SENSOR ANALYSIS</span>
        <span className="scan-readout-target">
          {scanData.targetName} — {scanData.targetType}
        </span>
      </div>

      <div className="scan-readout-body">
        {hasArcs ? (
          <ArcSilhouette arcs={scanData.shieldArcs!} />
        ) : (
          <div className="scan-readout-schematic">
            <span className="scan-readout-icon">{icon}</span>
          </div>
        )}

        <div className="scan-readout-data">
          {/* Primary stats */}
          {typeof scanData.hullIntegrity === "number" && (
            <div className={`scan-readout-row scan-row-${classify(scanData.hullIntegrity)}`}>
              <span className="scan-readout-label">HULL</span>
              <span className="scan-readout-value">{scanData.hullIntegrity}%</span>
            </div>
          )}
          {scanData.weaponsArmed !== undefined && (
            <div className={`scan-readout-row ${scanData.weaponsArmed ? "scan-row-warn" : ""}`}>
              <span className="scan-readout-label">WEAPONS</span>
              <span className="scan-readout-value">{scanData.weaponsArmed ? "ARMED" : "STANDBY"}</span>
            </div>
          )}
          {scanData.biosigns !== undefined && (
            <div className="scan-readout-row">
              <span className="scan-readout-label">BIOSIGNS</span>
              <span className="scan-readout-value">
                {scanData.biosigns === "unknown" ? "JAMMED" : scanData.biosigns}
              </span>
            </div>
          )}

          {/* Subsystem rows */}
          {subsystemRows.map(s => (
            <div key={s.name} className={`scan-readout-row scan-row-${classify(s.efficiency)}`}>
              <span className="scan-readout-label scan-readout-sublabel">{s.name}</span>
              <span className="scan-readout-value">{s.efficiency}%</span>
            </div>
          ))}

          {/* Free-form sections (cargo, anomalies, etc.) */}
          {(scanData.sections ?? []).map((section, i) => (
            <div key={i} className={`scan-readout-row ${section.status === "critical" ? "critical" : section.status === "warning" ? "warning" : ""}`}>
              <span className="scan-readout-label">{section.label}</span>
              <span className="scan-readout-value">{section.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Tactical notes summary at the bottom */}
      {scanData.tacticalNotes && scanData.tacticalNotes.length > 0 && (
        <ul className="scan-readout-notes">
          {scanData.tacticalNotes.slice(0, 3).map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      )}
    </div>
  );
}

function formatId(id: string): string {
  return id.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}
